import dayjs from "dayjs";
import {
  Calendar as CalendarIcon,
  Check,
  Gift,
  Link2,
  Mail,
  PlayCircle,
  User,
} from "lucide-react";
import { Button } from "@/v2/components/ui/button";
import type { BillingCycle } from "../../types";

export function StepConfirmation({
  formik,
  channelName,
  activeLabel,
  billing,
  termCount,
  orderNumber,
  orderDate,
  onBuyAnotherGift,
  onGoToSchedule,
  onReturnToAccount,
}: {
  formik: any;
  channelName: string;
  activeLabel: string;
  billing: BillingCycle;
  termCount: number;
  orderNumber: string | null;
  orderDate: string | null;
  onBuyAnotherGift: () => void;
  onGoToSchedule: () => void;
  onReturnToAccount: () => void;
}) {
  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="text-center pt-4 pb-2">
        <div className="relative inline-block">
          <span className="absolute -left-8 -top-3 size-1.5 rounded-full bg-blue-400" />
          <span className="absolute -right-9 -top-2 size-1.5 rounded-full bg-orange-400" />
          <span className="absolute -left-7 top-8 size-1 rounded-full bg-green-400" />
          <span className="absolute -right-7 top-9 size-1 rounded-full bg-pink-400" />
          <div className="size-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto">
            <Check className="size-8 stroke-[3]" />
          </div>
        </div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground mt-4">
          Your gift has been sent!
        </h1>
        <p className="text-sm mt-1 text-muted-foreground">
          We've emailed all the details to{" "}
          <span className="font-semibold text-foreground">
            {formik.values.name || "the recipient"}
          </span>
          . They can start enjoying {channelName} right away.
        </p>
      </div>

      {/* WHAT HAPPENS NEXT */}
      <div className="bg-card border border-border rounded-2xl p-6 flex items-start gap-4 shadow-xs">
        <div className="size-10 rounded-full bg-action/10 flex items-center justify-center shrink-0">
          <Gift className="size-5 text-action" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-foreground">
            What happens next?
          </p>
          <p className="text-sm text-muted-foreground mt-0.5">
            {formik.values.name || "The recipient"} will receive an email
            with:
          </p>
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2 text-sm text-foreground">
              <Mail className="size-4 text-action shrink-0" />
              Gift details and your personal message
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground">
              <Link2 className="size-4 text-action shrink-0" />
              A one-tap link to redeem their subscription
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground">
              <PlayCircle className="size-4 text-action shrink-0" />
              Instant access to {channelName}
            </div>
          </div>
        </div>
      </div>

      {/* GIFT DETAILS */}
      <div className="bg-card border border-border rounded-2xl p-6 shadow-xs">
        <p className="font-bold text-foreground mb-4">Gift details</p>
        <div className="text-sm">
          <div className="flex items-center justify-between py-2.5 border-b border-border">
            <span className="text-muted-foreground">Recipient</span>
            <span className="text-right">
              <span className="block font-bold text-foreground">
                {formik.values.name || "Recipient"}
              </span>
              <span className="block text-muted-foreground text-xs mt-0.5">
                {formik.values.email}
              </span>
            </span>
          </div>
          <div className="flex items-center justify-between py-2.5 border-b border-border">
            <span className="text-muted-foreground">Plan</span>
            <span className="font-bold text-foreground">
              {activeLabel} Plan (
              {billing === "annual"
                ? termCount > 1
                  ? `${termCount} Years`
                  : "1 Year"
                : termCount > 1
                  ? `${termCount} Months`
                  : "1 Month"}
              )
            </span>
          </div>
          <div className="flex items-center justify-between py-2.5 border-b border-border">
            <span className="text-muted-foreground">Length</span>
            <span className="font-bold text-foreground">
              {billing === "annual"
                ? termCount > 1
                  ? `${termCount} Years`
                  : "1 Year"
                : termCount > 1
                  ? `${termCount} Months`
                  : "1 Month"}
            </span>
          </div>
          <div className="flex items-center justify-between py-2.5 border-b border-border">
            <span className="text-muted-foreground">Delivery</span>
            <span className="font-bold text-foreground">
              {formik.values.send
                ? "Sent now"
                : dayjs(formik.values.date).format("MMM D, YYYY")}
            </span>
          </div>
          <div className="flex items-center justify-between py-2.5 border-b border-border">
            <span className="text-muted-foreground">Order number</span>
            <span className="font-bold text-foreground">#{orderNumber}</span>
          </div>
          <div className="flex items-center justify-between py-2.5">
            <span className="text-muted-foreground">Date</span>
            <span className="font-bold text-foreground">{orderDate}</span>
          </div>
        </div>
      </div>

      {/* ACTIONS */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onBuyAnotherGift}
          className="gap-1.5"
        >
          <Gift className="size-4" /> Buy Another Gift
        </Button>
        <Button
          type="button"
          onClick={onGoToSchedule}
          className="gap-1.5 bg-action text-white hover:bg-action-hover"
        >
          <CalendarIcon className="size-4" /> Go to Schedule
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onReturnToAccount}
          className="gap-1.5"
        >
          <User className="size-4" /> Return to Account
        </Button>
      </div>
    </div>
  );
}
